import React, {useCallback, useEffect, useRef, useState} from "react";
import GetDateFilter from "./GetDateFilter";
import getTimezoneOffset from "../../../../../utils/GetTimeZone";
import {SimpleSearch} from "../../../../../reduxToolkit/Search/SearchApi";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import TableFunction from "../../../../../components/table/TableFunction";
import AttendanceDayContainer from "../../../Dining/Manage/SubComponents/AttendanceDayContainer";
import {VIEW_TYPE} from "../../../../../enums/ViewType";
import {DEFAULT_LEFT, DEFAULT_PAGINATION_START, SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {ATTENDANCE_MODES} from "../../../Dining/Manage/Models/Model";
import {getFullName} from "../../../../../Apis/functions";
import {ATTENDANCE_COLOR_MODEL} from '../Model/Model';
import CustomTooltip from "../../../../../components/CustomTooltip";

/**
 * @Email azapata@ibl.mx
 * @Author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 * @createdAt 2025-10-07
 */

/**
 * Custom hook to manage attendance period table state and rendering.
 *
 * Handles:
 * - Attendance data fetching and filtering by date range
 * - Table head and body generation
 * - Pagination and search
 * - Rendering attendance day containers dynamically
 *
 * @param {Object} props - Hook/component parameters
 * @param {string} props.selectedOption - Current attendance mode (e.g., daily, weekly)
 * @param {string} props.searchElement - Search keyword
 * @param {Object} props.option - Additional options for search or table rendering
 * @param {Function} props.setRender - Function to set dynamic content rendering (e.g., AttendanceDayContainer)
 * @param {string} props.color - Theme color for table highlights
 * @param {Function} props.setBodyForExport - Function to prepare table data for export
 *
 *
 */


export const AttendancePeriodTableApp = ({
                                             selectedOption,
                                             searchElement,
                                             option,
                                             setRender,
                                             color,
                                             setBodyForExport,
                                             modified,
                                             setModified
                                         }) => {

    const dispatch = useDispatch();
    const {t} = useTranslation();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const { getAttendanceDate } = GetDateFilter();
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const { user } = useSelector((state) => state.authenticatioauthennSlice);
    const userId = user?.data?.id;
    const [attendanceStartDate, setAttendanceStartDate] = useState(getAttendanceDate(selectedOption).start);
    const [attendanceEndDate, setAttendanceEndDate] = useState( getAttendanceDate(selectedOption).end);
    const [tableId] = useState('tbl_aw_attendance_daily');
    const [searchId] = useState('txt_aw_search_attendance_daily');
    const headRef = useRef();
    const leftPosition = DEFAULT_LEFT;
    const firstTime = useRef(true);
    // the axios interceptor drops the global overlay as soon as the response lands, but the day
    // columns are laid out several frames later: this covers that gap with our own loader
    const [loadingDays, setLoadingDays] = useState(false);

    const hideDaysLoader = useCallback(() => setLoadingDays(false), []);

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head w-190px">{t("name")}</CustomTh>,
            <CustomTh id="td_dat_second_column" tableId={tableId} headRef={headRef}/>,
        ]
    }

    const getTbody = () => {
        if (!results?.content?.length)
            return  { body: [], data:[] };

        const body = results?.content.map((member) => {
            return [
                <CustomTd key="name" tableId={tableId} fixed={true} left={leftPosition} tooltip={true}  classes="h-90px first-column w-190px border-shades-200">
                    <CustomTooltip
                        text= {member?.fullName || `${getFullName(member?.userName, member?.userLastName, member?.userSecondLastName)}`}
                        classes={`fs-14px text-ellipsis-2-rows`}
                        showIfTruncated={true}
                    />
                </CustomTd>,
                <CustomTd key="days" tableId={tableId} classes={"h-90px"}/>,

            ]
        })

        return {
            body,
            data:results?.content
        }
    }

    /**
     * Keeps the floating day grid in sync with the current page.
     *
     * It lives outside getTbody on purpose: that function is invoked while the
     * table is building its rows, so setting state from inside its map coupled
     * the grid to the iteration and rebuilt it on every pass.
     */
    useEffect(() => {
        if (!results?.content?.length) {
            setRender('');
            setLoadingDays(false);
            return;
        }

        setRender(
            <AttendanceDayContainer
                color={color}
                currentDays={results.content[0]?.attendances}
                results={results.content}
                tableId={tableId}
                headRef={headRef}
                model={ATTENDANCE_COLOR_MODEL}
                showScheduledShift={true}
                onRendered={hideDaysLoader}
            />
        );
    }, [results, color, tableId, setRender, hideDaysLoader]);

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, { type, searchElement }) => {
        const aux = type ?? selectedOption;

        // On mount with an active simple search, SearchSimple already triggers the request. Avoid duplicating it.
        if (firstTime.current) {
            firstTime.current = false;
            if (searchElement) return;
        }

        setRender('');
        let { start, end } = getAttendanceDate(aux);
        setAttendanceStartDate(start);
        setAttendanceEndDate(end);
        const timezone = getTimezoneOffset();

        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            attendanceTimeZone: timezone,
            attendanceStartDate: start,
            attendanceEndDate: end,
            attendanceSearchType: aux,
            model: option,
            key: searchElement
        });
        setBodyForExport({ ...body,  userId });
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (!success) return;
            // takes over right as the interceptor hides the global overlay
            setLoadingDays(true);
            setResults(data);
        });
    };


    const {thead, tbody, pagination, page, setPage, rowsPerPage } = TableFunction({
        data: results,
        getThead,
        getTbody,
        view: VIEW_TYPE.LIST.name,
        labelRowsPerPage: 'attendance_per_page',
        fixedPagination: false,
        hasContainer: true,
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
        dependencies: [color]
    });

    const handleChange = (e) => {
        const type = e.target.value;

        // Switching to daily unmounts this table; the daily table fetches on mount
        if(type === ATTENDANCE_MODES.daily) return;

        getData(DEFAULT_PAGINATION_START, { type, searchElement });
        setPage(DEFAULT_PAGINATION_START);
    }

    return {
        attendanceStartDate,
        attendanceEndDate,
        page,
        rowsPerPage,
        tableId,
        thead,
        tbody,
        color,
        t,
        pagination,
        searchId,
        setResults,
        selectedDate,
        setSelectedDate,
        handleChange,
        getData,
        loadingDays,
    }
}
